'use client';

import Link from 'next/link';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@zcmss/ui';
import type { Plan } from '@zcmss/types';
import { Check } from 'lucide-react';

interface PricingCardsProps {
  plans: Plan[];
}

const fallbackPlans: Plan[] = [
  {
    id: '1',
    name: 'Starter',
    slug: 'starter',
    price_monthly: '29.00',
    price_yearly: '290.00',
    features: ['crm', 'tasks', 'reports_basic'],
    limits: { max_users: 5 },
    is_active: true,
  },
  {
    id: '2',
    name: 'Popular',
    slug: 'popular',
    price_monthly: '99.00',
    price_yearly: '990.00',
    features: ['crm', 'marketing', 'whatsapp', 'automation'],
    limits: { max_users: 25 },
    is_active: true,
  },
  {
    id: '3',
    name: 'Advance',
    slug: 'advance',
    price_monthly: '299.00',
    price_yearly: '2990.00',
    features: ['crm', 'marketing', 'whatsapp', 'automation', 'custom_domain'],
    limits: { max_users: -1 },
    is_active: true,
  },
];

export function PricingCards({ plans }: PricingCardsProps) {
  const displayPlans = plans.length > 0 ? plans : fallbackPlans;

  return (
    <div className="mt-12 grid gap-8 lg:grid-cols-3">
      {displayPlans.map((plan, index) => (
        <Card
          key={plan.id}
          className={index === 1 ? 'border-indigo-500 ring-2 ring-indigo-500/20' : ''}
        >
          <CardHeader>
            {index === 1 && (
              <span className="mb-2 w-fit rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                Most Popular
              </span>
            )}
            <CardTitle>{plan.name}</CardTitle>
            <CardDescription>
              <span className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
                ${plan.price_monthly}
              </span>
              <span className="text-zinc-500">/month</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                  <Check className="h-4 w-4 text-indigo-600" />
                  {feature.replace(/_/g, ' ')}
                </li>
              ))}
            </ul>
            <Link href={`/register?plan=${plan.slug}`} className="mt-8 block">
              <Button className="w-full" variant={index === 1 ? 'default' : 'outline'}>
                Get Started
              </Button>
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
